import type { TextVariable } from '@carboncopyco/types';
import { Button, TextCaption } from '@carboncopyco/ui';
import { slugifyText } from '@carboncopyco/utils';
import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import type { InferType } from 'yup';
import { useCopy } from '../../hooks';
import { VariableFormInput } from '../VariableFormInput';
import type { VariableFormBaseProps } from '../types';
import { useFormValidation } from '../useFormValidation';
import { VariableCodeField } from './VariableCodeField';
import { useFormSchema } from './useFormSchema';

type FormFields = Pick<TextVariable, 'name' | 'value' | 'description' | 'code'>;

export interface TextVariableFormProps extends VariableFormBaseProps {
  variable?: TextVariable;
}

const initialValues: FormFields = {
  name: '',
  value: '',
  description: '',
  code: '',
};

const getInitialValues = (variable: TextVariable): FormFields => {
  if (variable) {
    return {
      name: variable.name,
      value: variable.value,
      description: variable.description,
      code: variable.code,
    };
  }

  return initialValues;
};

export const TextVariableForm = ({
  autoFocus = false,
  onFocus,
  onCancel,
  onSubmit,
  variable,
  variables = [],
}: TextVariableFormProps) => {
  const { copyText } = useCopy();
  const [formValues, setFormValues] = useState<FormFields>(
    getInitialValues(variable)
  );
  const inputRef = useRef<HTMLInputElement>();
  const schema = useFormSchema({ variable, variables });
  const [errors, setErrors] = useState<InferType<typeof schema>>(initialValues);
  const validateForm = useFormValidation(schema, setErrors);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const onSubmitHandler = async () => {
    const isFormValid = await validateForm(formValues);
    if (!isFormValid) return;

    const newVariable: TextVariable = {
      type: 'text',
      value: formValues.value.trim(),
      name: formValues.name.trim(),
      code: formValues.code.trim(),
      description: formValues.description.trim(),
    };

    try {
      await onSubmit(newVariable);

      if (!variable) {
        copyText(
          `{{${newVariable.code}}}`,
          'Variable was created and copied to your clipboard.'
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col gap-4 relative">
      <div className="flex flex-col gap-3.5">
        <VariableFormInput
          label="Variable"
          name="name"
          value={formValues.name}
          ref={inputRef}
          autoFocus={autoFocus}
          onFocus={onFocus}
          onChange={(e) => {
            setFormValues((prev) => ({
              ...prev,
              name: e.target.value,
              code: slugifyText(e.target.value),
            }));
            setErrors((prev) => ({
              ...prev,
              name: '',
              code: '',
            }));
          }}
          error={errors.name}
        />
        <div className="flex flex-col gap-2">
          <VariableCodeField
            value={slugifyText(formValues.name)}
            showCopyButton={Boolean(variable)}
          />
          {errors.code && (
            <TextCaption
              color="text-ds-danger"
              label={errors.code}
            />
          )}
        </div>
        <div className="order-1">
          <VariableFormInput
            label="Default Value"
            name="value"
            value={formValues.value}
            onChange={(e) => {
              setFormValues((prev) => ({
                ...prev,
                value: e.target.value,
              }));
              setErrors((prev) => ({ ...prev, value: '' }));
            }}
            error={errors.value}
          />
        </div>
        <div className="order-2">
          <VariableFormInput
            label="Description"
            name="description"
            value={formValues.description}
            onChange={(e) => {
              setFormValues((prev) => ({
                ...prev,
                description: e.target.value,
              }));
              setErrors((prev) => ({
                ...prev,
                description: '',
              }));
            }}
            error={errors.description}
          />
        </div>
      </div>
      <div className="flex flex-row gap-2.5 justify-end">
        <Button
          label="Cancel"
          onClick={onCancel}
          highContrast
        />
        <Button
          label={variable ? 'Save' : 'Add & Copy'}
          onClick={onSubmitHandler}
          disabled={
            (variable &&
              isEqual(variable, {
                ...variable,
                ...formValues,
              })) ||
            Object.values(errors).some((error) => error) ||
            !formValues.name ||
            !formValues.value
          }
        />
      </div>
    </div>
  );
};
